import React, { useState } from 'react';
import { Zap, LayoutDashboard, Lightbulb, Store, BarChart3, MapPin, Users, Newspaper, Mail, FileText, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import NewsletterModal from '../components/NewsletterModal';
import SEO from '../components/SEO';

export default function About() {
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);

  const stats = [
    { number: '1,600+', label: 'Stations Tracked' },
    { number: '3', label: 'Counties Covered' },
    { number: '30', label: 'Years Combined Experience' },
    { number: '50,000+', label: 'Station Data Points' }
  ];

  const liveFeatures = [
    {
      icon: <Zap className="w-6 h-6" />,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      title: 'Automated Valuation',
      description: 'Institutional-grade fee simple valuations using our Gross Profit Multiple methodology. Input station details, get a defensible value backed by real market data — in minutes, not weeks.'
    },
    {
      icon: <LayoutDashboard className="w-6 h-6" />,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      title: 'Dashboard',
      description: 'Your command center. Retrieve past valuations, run side-by-side comparisons, and track market movement across your portfolio or pipeline.'
    },
    {
      icon: <Lightbulb className="w-6 h-6" />,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      title: 'Insights',
      description: "Market intelligence and analysis from people who actually appraise gas stations — not recycled press releases. Fuel margins, regulatory shifts, and deal trends that matter."
    }
  ];

  const roadmapFeatures = [
    {
      icon: <Store className="w-5 h-5" />,
      title: 'Marketplace',
      description: 'A curated listing platform for gas station acquisitions and dispositions — connecting buyers, sellers, and brokers with valuation data already baked in.'
    },
    {
      icon: <BarChart3 className="w-5 h-5" />,
      title: 'Research & Analysis',
      description: 'Deep-dive market reports on fuel retail corridors, submarket performance, and supply-demand dynamics across Southern California and beyond.'
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      title: 'Site Analysis',
      description: 'Granular property-level intelligence — traffic patterns, competitor mapping, demographic overlays, and highest-and-best-use scoring. A powerful greenfielding tool for feasibility studies.'
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: 'Vendor Directory',
      description: 'A vetted network of environmental consultants, fuel equipment suppliers, POS providers, and other specialists that gas station buyers and operators actually need.'
    },
    {
      icon: <Newspaper className="w-5 h-5" />,
      title: 'Industry News',
      description: 'Curated coverage of fuel retail — CARB regulations, branded vs. unbranded dynamics, M&A activity, and supply chain developments, filtered for signal over noise.'
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: 'Lease Fee Analysis',
      description: 'Our second valuation product — helping landlords and tenants determine fair market rent for fuel retail properties. A purpose-built tool for lease negotiations and rent reviews.'
    }
  ];

  return (
    <>
      <SEO
        title="About — Gas Station Valuation Platform | Fuel Central"
        description="Fuel Central is the first automated valuation model purpose-built for retail fuel properties. Built by industry professionals with 30+ years of gas station valuation experience."
        keywords="gas station valuation, fuel station appraisal, petroleum retail valuation, gas station market value"
      />

      <div className="w-full">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="container mx-auto px-4 py-24 md:py-32">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
                Gas Station Valuations<br />
                Built on Data, <span className="text-white">Not Guesswork</span>
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 leading-relaxed max-w-3xl mx-auto mb-10">
                Born from 30+ years of gas station appraisal experience, backed by leading brokerages supporting in excess of $10bn in deals. Fuel Central is the first automated valuation model purpose-built for retail fuel properties.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/valuation"
                  className="inline-flex items-center gap-2 bg-white text-blue-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors"
                >
                  Run a Valuation
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:border-white/60 transition-colors"
                >
                  View Pricing
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-200">
              {stats.map((stat, index) => (
                <div key={index} className="text-center py-10 px-4">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">{stat.number}</div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Story Section — two-column with accent bar */}
        <div className="bg-white">
          <div className="container mx-auto px-4 py-20">
            <div className="max-w-5xl mx-auto grid md:grid-cols-[auto_1fr] gap-8">
              {/* Left accent */}
              <div className="hidden md:block w-1 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />

              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Why We Built This</h2>
                <div className="space-y-5 text-gray-600 text-lg leading-relaxed">
                  <p>
                    Gas station valuation has always been opaque. Brokers pull comps from memory. Lenders rely on outdated multipliers. Buyers guess at fuel margins using national averages that have nothing to do with what's actually happening at the rack in Carson or Colton.
                  </p>
                  <p>
                    <strong className="text-gray-900">We saw an opportunity to change that.</strong> With the democratization of AI, we could finally translate decades of appraisal expertise into programmatic steps and take the guesswork out. Fuel Central's automated valuation model is powered by forward-looking fuel profit margins, zone-based freight calculations, brand tier adjustments, and three-leg volume estimation. No rules of thumb. No gut feel.
                  </p>
                  <p>
                    We currently cover Orange, Riverside, and San Bernardino Counties, with Los Angeles, San Diego, Imperial, Ventura, Santa Barbara, and Kern Counties on the way. Every station is tracked with live retail neighborhood pricing from our sample surveys, localized rack data, and market-level operating comparables.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Live Features Section */}
        <div className="bg-gray-50">
          <div className="container mx-auto px-4 py-20">
            <div className="text-center mb-14">
              <span className="inline-block text-sm font-bold tracking-wider uppercase text-green-700 bg-green-100 px-4 py-1.5 rounded-full mb-4">
                Available Now
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">What You Can Do Today</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {liveFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl p-8 hover:border-blue-300 hover:shadow-lg transition-all duration-200"
                >
                  <div className={`w-14 h-14 ${feature.iconBg} ${feature.iconColor} rounded-xl flex items-center justify-center mb-5`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Roadmap Section */}
        <div className="bg-white">
          <div className="container mx-auto px-4 py-20">
            <div className="text-center mb-14">
              <span className="inline-block text-sm font-bold tracking-wider uppercase text-amber-700 bg-amber-100 px-4 py-1.5 rounded-full mb-4">
                On the Horizon
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">What's Coming Next</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {roadmapFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl p-6 hover:border-amber-300 hover:shadow-md transition-all duration-200 relative overflow-hidden group"
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="flex items-center gap-3 mb-3">
                    <div className="text-amber-600">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gray-50">
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Be the First to Know</h2>
              <p className="text-gray-600 mb-8 text-lg">
                Get early access to new features as we roll them out — plus market intel you won't find anywhere else.
              </p>
              <button
                onClick={() => setShowNewsletterModal(true)}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-lg"
              >
                <Mail className="w-5 h-5" />
                Get Early Access
              </button>
              <p className="text-sm text-gray-500 mt-4">
                No spam. Just product updates and the occasional fuel market insight.
              </p>
            </div>
          </div>
        </div>

        {/* Newsletter Modal */}
        <NewsletterModal
          isOpen={showNewsletterModal}
          onClose={() => setShowNewsletterModal(false)}
        />
      </div>
    </>
  );
}
